import {
  ActionIcon,
  Flex,
  Title,
  Tooltip,
  useMantineTheme,
  rem,
  Popover,
  Text,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { IconPlus } from "../../../shared/svg/AddIcon";

import { CalendarModal } from "./CalendarModal";
import useDevelopmentStore from "../../../store/useDevelopmentStore";
import { LearningPlan } from "../../../types/api";
import { useTranslation } from "react-i18next";
import { CalendarSelectWithIcons } from "./CustomSelect";
import { IconEye } from "../../../shared/svg/IconEye";
import { OnboardingTour } from "@gfazioli/mantine-onboarding-tour";

export function CalendarSelector() {
  const [modalOpened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState(false);
  const [openedAdd, setOpenedAdd] = useState(false);
  const [openedEye, setOpenedEye] = useState(false);

  const { t } = useTranslation();
  const {
    learningPlan,
    addLearningPlan,
    setCurrentLearningPlanId,
    currentPlanId,
    editLearningPlan,
    removeLearningPlan,
    fetchDevelopmentPlan,
  } = useDevelopmentStore();
  const [formErrors, setFormErrors] = useState<{
    title?: string;
  }>({});
  const [formData, setFormData] = useState<LearningPlan>({
    id: undefined,
    title: "",
    description: "",
    deadline: "",
    skills_to_gain: [],
    estimated_total_hours: 0,
    motivation_note: "",
    coaching_notes: "",
    status: "planned",
    progress: 0,
    total_goals: 0,
    completed_goals: 0,
  });
  const [selectedCalendarId, setSelectedCalendarId] = useState<number | null>(
    currentPlanId || null
  );

  const theme = useMantineTheme();
  const isMobile = useMediaQuery("(pointer: coarse)");

  const commonStyles: React.CSSProperties = {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.dark[7],
    fontSize: rem(14),
    padding: `${rem(4)} ${rem(8)}`,
    borderRadius: rem(4),
    boxShadow: theme.shadows.sm,
    maxWidth: isMobile ? rem(300) : rem(500),
    wordWrap: "break-word" as "break-word",
    whiteSpace: "normal",
  };

  useEffect(() => {
    if (!selectedCalendarId && learningPlan.length > 0) {
      setSelectedCalendarId(learningPlan[0].id ?? null);
    }
  }, [learningPlan]);

  const resetForm = () =>
    setFormData({
      id: undefined,
      title: "",
      description: "",
      deadline: "",
      skills_to_gain: [],
      estimated_total_hours: 0,
      motivation_note: "",
      coaching_notes: "",
      status: "planned",
      progress: 0,
      total_goals: 0,
      completed_goals: 0,
    });

  const handleAdd = () => {
    setEditing(false);
    resetForm();
    setFormErrors({});
    open();
  };

  const handleEdit = async () => {
    const res = await fetchDevelopmentPlan();
    if (!selectedCalendarId) return;
    const calendar = res.find((c) => c.id === selectedCalendarId);
    if (!calendar) return;
    setFormData(calendar);
    setEditing(true);
    open();
    setFormErrors({});
  };

  const handleSave = async () => {
    setFormErrors({});

    const errors: { title?: string } = {};

    if (!formData.title.trim()) {
      errors.title = t("field_required");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      const newData = formData;
      if (newData.deadline === "") {
        delete newData.deadline;
      }

      if (formData.title && !editing) {
        const res = await addLearningPlan(newData);
        if (res.id) {
          setCurrentLearningPlanId(res.id);
          setSelectedCalendarId(res.id);
        }
      } else if (editing) {
        if (!newData.deadline) {
          newData.deadline = null;
        }
        await editLearningPlan(newData);
      }

      close();
    } catch (err) {
      console.log(err);
    }
  };

  const handleDelete = async (id: number) => {
    const avaliableId = learningPlan.filter((i) => i.id !== id)[0]?.id;
    try {
      await removeLearningPlan(id);
      setCurrentLearningPlanId(avaliableId!);
      setSelectedCalendarId(avaliableId!);

      close();
    } catch (err) {
      console.log(err);
    }
  };

  const handleClose = () => {
    setFormErrors({});
    close();
  };
  const matches = useMediaQuery("(max-width: 650px)");

  return (
    <>
      <Title order={2} mb={20}>
        {t("learning_plan")}
      </Title>
      <Flex align="center" gap={10} w="100%">
        <OnboardingTour.Target id="select-calendar">
          <div>
            <CalendarSelectWithIcons
              data={learningPlan.map((c) => ({
                value: String(c.id),
                label: c.title,
                autogenerated: c.autogenerated,
              }))}
              value={currentPlanId?.toString() || null}
              onChange={(val) => {
                if (val !== null) {
                  const numVal = Number(val);
                  setCurrentLearningPlanId(numVal);
                  setSelectedCalendarId(numVal);
                }
              }}
            />
          </div>
        </OnboardingTour.Target>

        {matches ? (
          <>
            <OnboardingTour.Target id="add-calendar">
              <div id="add-calendar">
                <ActionIcon
                  variant="light"
                  w={36}
                  h={36}
                  color="green"
                  onClick={handleAdd}
                  style={{ flexShrink: 0 }}
                >
                  <IconPlus />
                </ActionIcon>
              </div>
            </OnboardingTour.Target>

            {learningPlan?.find((el) => el.id === selectedCalendarId)?.title !==
              "Default Learning Plan" && (
              <OnboardingTour.Target id="edit-calendar">
                <div id="edit-calendar">
                  <ActionIcon
                    variant="light"
                    color="violet"
                    w={36}
                    h={36}
                    onClick={handleEdit}
                    disabled={!selectedCalendarId}
                    style={{ flexShrink: 0 }}
                  >
                    <IconEye />
                  </ActionIcon>
                </div>
              </OnboardingTour.Target>
            )}
          </>
        ) : (
          <>
            {" "}
            <OnboardingTour.Target id="add-calendar">
              <div
                id="add-calendar"
                onMouseEnter={() => setOpenedAdd(true)}
                onMouseLeave={() => setOpenedAdd(false)}
              >
                <Popover
                  opened={openedAdd}
                  onClose={() => setOpenedAdd(false)}
                  withArrow
                  position="top"
                  offset={6}
                  arrowSize={6}
                  arrowOffset={8}
                  transitionProps={{ transition: "fade", duration: 100 }}
                  styles={{
                    dropdown: commonStyles,
                    arrow: { backgroundColor: theme.colors.gray[1] },
                  }}
                >
                  <Popover.Target>
                    <ActionIcon
                      variant="light"
                      w={36}
                      h={36}
                      color="green"
                      onClick={handleAdd}
                      style={{ flexShrink: 0 }}
                    >
                      <IconPlus />
                    </ActionIcon>
                  </Popover.Target>

                  <Popover.Dropdown>
                    <Text size="sm">{t("add_new_calendar_tooltip")}</Text>
                  </Popover.Dropdown>
                </Popover>
              </div>
            </OnboardingTour.Target>
            {learningPlan?.find((el) => el.id === selectedCalendarId)?.title !==
              "Default Learning Plan" && (
              <OnboardingTour.Target id="edit-calendar">
                <div
                  id="edit-calendar"
                  onMouseEnter={() => setOpenedEye(true)}
                  onMouseLeave={() => setOpenedEye(false)}
                >
                  <Popover
                    position="top"
                    offset={6}
                    withArrow
                    arrowSize={6}
                    arrowOffset={8}
                    opened={openedEye}
                    onClose={() => setOpenedEye(false)}
                    transitionProps={{ transition: "fade", duration: 100 }}
                    styles={{
                      dropdown: commonStyles,
                      arrow: { backgroundColor: theme.colors.gray[1] },
                    }}
                  >
                    <Popover.Target>
                      <ActionIcon
                        variant="light"
                        color="violet"
                        w={36}
                        h={36}
                        onClick={handleEdit}
                        disabled={!selectedCalendarId}
                        style={{ flexShrink: 0 }}
                      >
                        <IconEye />
                      </ActionIcon>
                    </Popover.Target>

                    <Popover.Dropdown>
                      <Text size="sm">{t("see_plan_details")}</Text>
                    </Popover.Dropdown>
                  </Popover>
                </div>
              </OnboardingTour.Target>
            )}
          </>
        )}
      </Flex>

      <CalendarModal
        opened={modalOpened}
        onClose={handleClose}
        data={formData}
        onChange={setFormData}
        onSave={handleSave}
        onDelete={editing ? handleDelete : undefined}
        isEditing={editing}
        errors={formErrors}
        setFormErrors={setFormErrors}
      />
    </>
  );
}
